import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import {
  getAdminStore,
  useAuthSession,
  type Store,
  type StoreMemberRole,
  type SupabaseClient,
} from '@dukanti/shared';
import { supabase } from '@/lib/supabase';

/**
 * F-007 §4.4 / F-063 — shared admin hook that reads the current
 * merchant's full store row through a member-gated RPC.
 *
 * ## Why this exists
 *
 * Both `/dashboard` and `/dashboard/settings` need the full `Store` row,
 * and both use `['store', storeId]` as the React Query key. If the two
 * routes inlined their own `useQuery` calls, they'd each carry a copy of
 * the column projection — and F-004's M1 invariant is that every cache
 * writer (publish / unpublish / contact-methods update) shares byte-for-
 * byte column alignment with every cache reader. Centralising the SELECT
 * here means the single projection below is the only place the column
 * list can drift.
 *
 * ## M1 cache-shape contract (LOAD-BEARING)
 *
 * The result MUST project every column in the `Store` type so that
 * `setQueryData(['store', storeId], row)` from
 * `useUpdateStoreContactMethods` / `usePublishStore` stays byte-compatible.
 * If this ever narrows, the next background refetch (window focus,
 * staleTime expiry) will silently wipe the widened columns from the
 * cache — and AC8 (F-004) + AC14 (F-007) will regress the moment the
 * user backgrounds the tab.
 *
 * Keep this RPC result in lockstep with:
 *   - `Store` in `packages/shared/src/types/store.ts`
 *   - `updateStoreContactMethods` service `.select()` list
 *   - `publish_store` RPC SELECT projection
 *
 * ## Cross-tab cache isolation (F-007-cross-tab-cache-comment)
 *
 * React Query's `QueryClient` is scoped to the current browser tab's
 * React tree. If a merchant has the admin dashboard open in two tabs
 * and updates contact info in one, the mutation's `onSuccess` writes
 * the new row into that tab's cache via `setQueryData` -- but the
 * other tab's cache still holds the stale row. This is expected React
 * Query behavior, not a bug.
 *
 * Convergence happens automatically: `refetchOnWindowFocus` defaults
 * to `true`, so the stale tab refetches `['store', storeId]` the
 * moment it regains focus. No `BroadcastChannel` or cross-tab sync
 * mechanism is needed for the admin store cache -- the window-focus
 * refetch is sufficient for the admin's single-merchant, low-frequency
 * mutation pattern.
 */
export function useStore(storeId: string | null): UseQueryResult<Store> {
  return useQuery<Store>({
    queryKey: ['store', storeId],
    enabled: !!storeId,
    queryFn: async () => {
      if (!storeId) throw new Error('unreachable: enabled guard');
      return getAdminStore(supabase, storeId);
    },
  });
}

export interface StoreNewsletterCollectionPermissions {
  readonly role: StoreMemberRole | null;
  readonly canManageCollection: boolean;
}

/** UI eligibility only: the setting RPC independently enforces Admin identity and role. */
export function useStoreNewsletterCollectionPermissions(
  storeId: string | null,
  client: SupabaseClient = supabase,
): UseQueryResult<StoreNewsletterCollectionPermissions> {
  const session = useAuthSession(client);
  const userId = session.user?.id ?? null;
  const isAdminIdentity = Boolean(session.user?.email && !session.user?.phone);
  return useQuery({
    queryKey: ['store-newsletter-collection-permissions', storeId, userId],
    enabled: Boolean(storeId && userId && !session.isLoading),
    staleTime: 30_000,
    queryFn: async (): Promise<StoreNewsletterCollectionPermissions> => {
      if (!storeId || !userId) throw new Error('unreachable: enabled guard');
      if (!isAdminIdentity) return { role: null, canManageCollection: false };
      const { data, error } = await client
        .from('store_members')
        .select('role')
        .eq('store_id', storeId)
        .eq('user_id', userId)
        .is('deleted_at', null)
        .maybeSingle<{ role: StoreMemberRole }>();
      if (error) throw error;
      const role = data?.role ?? null;
      return { role, canManageCollection: role === 'owner' || role === 'admin' };
    },
  });
}
